import { BodySmall, Box } from '@parspec/pixel';

import { SortProductListRequest } from '../queries/apiTypes';
import { PricingLeadTimeTabDisabledLabel } from './components';

export const TAB_VALUES = {
    SPECIFICATION_TAB_VALUE: 'specification',
    LEAD_TAB_VALUE: 'leadTime',
    DATASHEET_TAB_VALUE: 'dataSheets',
    DOCUMENTS_TAB_VALUE: 'documents',
    FINAL_DELIVERABLES_TAB_VALUE: 'finalDeliverables',
    PRICING_AND_LEAD_TIME: 'pricingAndLeadtime'
};

export const TAB_SUPPORTING_IMPORT_PRODUCTS = [
    TAB_VALUES.SPECIFICATION_TAB_VALUE,
    TAB_VALUES.PRICING_AND_LEAD_TIME,
    TAB_VALUES.DOCUMENTS_TAB_VALUE
] as const;

export type TabSupportImportProducts = typeof TAB_SUPPORTING_IMPORT_PRODUCTS[number];

export const PRICING_AND_LEADTIME_ENABLED_TAB_OPTION = { label: 'Pricing & Lead Time', value: TAB_VALUES.PRICING_AND_LEAD_TIME };

const PRICING_AND_LEADTIME_DISABLED_TAB_OPTION = {
    label: <PricingLeadTimeTabDisabledLabel />,
    value: TAB_VALUES.PRICING_AND_LEAD_TIME
};

export const TAB_OPTIONS = [
    { label: 'Specification', value: TAB_VALUES.SPECIFICATION_TAB_VALUE },
    {
        label: (
            <Box display="flex" alignItems="center" gap="4px" data-testid="additional-documents-beta-label">
                <BodySmall color="inherit" fontWeight="400">
                    Documents
                </BodySmall>{' '}
            </Box>
        ),
        value: TAB_VALUES.DOCUMENTS_TAB_VALUE
    },
    { label: 'Final Deliverables', value: TAB_VALUES.FINAL_DELIVERABLES_TAB_VALUE }
];

export const PRICING_ENABLED_TAB_OPTIONS = [...TAB_OPTIONS];
PRICING_ENABLED_TAB_OPTIONS.splice(1, 0, PRICING_AND_LEADTIME_ENABLED_TAB_OPTION);

export const PRICING_DISABLED_TAB_OPTIONS = [...TAB_OPTIONS];
PRICING_DISABLED_TAB_OPTIONS.splice(1, 0, PRICING_AND_LEADTIME_DISABLED_TAB_OPTION);



export const EXPORT_OPTIONS = (onClick: (type: string) => void) => [
    { label: 'CSV', onClick: () => onClick('csv') },
    { label: 'Excel', onClick: () => onClick('xls') },
    { label: 'Agile XML', onClick: () => onClick('agile_xml') }
];

export const CREATE_SECTION_OPTIONS = (onClick: (type: string) => void) => [
    { label: 'Products section', onClick: () => onClick('products') },
    { label: 'Options section', onClick: () => onClick('options') }
];

export const CREATE_DOCUMENT_OPTIONS = (onClick: (type: string) => void) => [
    { label: 'Create Submittal', onClick: () => onClick('submittal') },
    { label: 'Create O&M', onClick: () => onClick('o_m') }
];

export const DocumentTypes = {
    DATASHEET: 'datasheet',
    WARRANTY: 'warranty',
    DRAWING: 'drawing',
    INSTALLATION_GUIDE: 'installation_guide',
    OTHER_DOCUMENT: 'other_document'
};

export enum Statuses {
    confirmed = 'CONFIRMED',
    inProgress = 'IN_PROGRESS',
    notConfirmed = 'NOT_CONFIRMED'
}
export const SubmittalBomType = 'submittal_package';

type OnOptionClick = (sortBy: SortProductListRequest) => void;

export const sortOptions = (onOptionClick: OnOptionClick) => [
    { label: 'Type (A-Z)', onClick: () => onOptionClick({ sorting_field_name: 'category', sorting_order: 'asc' }) },
    { label: 'Type (Z-A)', onClick: () => onOptionClick({ sorting_field_name: 'category', sorting_order: 'desc' }) },
    { label: 'Manufacturer (A-Z)', onClick: () => onOptionClick({ sorting_field_name: 'manufacturer', sorting_order: 'asc' }) },
    { label: 'Manufacturer (Z-A)', onClick: () => onOptionClick({ sorting_field_name: 'manufacturer', sorting_order: 'desc' }) }
];

export const SCHEDULE_FILE_TYPES = {
    xmlFileType: '.xml',
    xlsxFileType: '.xlsx',
    xlsFileType: '.xls',
    csvFileType: '.csv',
    xlsmFileType: '.xlsm'
};
export const RECOGNISED_EXCEL_FOMRATS = {
    OASIS_EXCEL: 'oasis_excel',
    EPISOR_EXCEL: 'epicor_excel',
    AGILE_EXCEL: 'agile_excel',
    NORMAL_EXCEL: 'normal_excel',
    PARSPEC_EXCEL: 'parspec_quote_excel'
};

//Kit Creation Error Dialog Text
export const KIT_CREATE_ERROR_TEXT = 'We weren’t able to create a kit with the selected line(s) because one or more of them is already a kit.';
export const KIT_CREATE_ERROR_HELPER_TEXT = 'Please de-select all kit lines and try again.';
export const KIT_CREATE_ERROR_OPTION_TEXT =
    'We weren’t able to create a kit with the selected line(s) because one or more of them has one or more associated options in it. Please remove those line(s) and try creation kit.';
export const KIT_CREATE_ERROR_OPTION_HELPER_TEXT = 'Please de-select all option lines and try again.';

export enum KIT_ERROR_TYPE {
    KIT = 'kit',
    OPTION = 'option'
}

export const KIT_ERROR = {
    [KIT_ERROR_TYPE.KIT]: { text: KIT_CREATE_ERROR_TEXT, helperText: KIT_CREATE_ERROR_HELPER_TEXT },
    [KIT_ERROR_TYPE.OPTION]: { text: KIT_CREATE_ERROR_OPTION_TEXT, helperText: KIT_CREATE_ERROR_OPTION_HELPER_TEXT }
};

//Kit popup description text
export const PRICE_LEAD_TIME_DESCRIPTION_TEXT =
    'Enter the information for this Kit and its components. The cost cells for the Kit line will be automatically filled based on the cost of the Component lines, and only the Kit line will appear on the Pricing & Lead Time tab.';
export const BOM_DESCRIPTION_TEXT = 'Enter the information for this Kit and its components. Only the Kit line will appear on the BOM tab.';
export const DATASHEET_DESCRIPTION_TEXT = 'Enter the information for this Kit and its components. Only the Kit line will appear on the Datasheet tab.';
export const OM_DESCRIPTION_TEXT = 'Enter the information for this Kit and its components. Only the Kit line will appear on the Other Documents tab.';

export const PARSEC_EXCEL_IMPORT_WARNING = 'Importing a Parspec Excel quote will NOT import Tax, or Freight values.';

export enum SECTION_TYPE {
    MAIN = 'primary',
    OPTION = 'substitute'
}

export const COLUMN_DETAILS_OM_TAB = {
    order: [
        'category',
        'qty',
        'manufacturer',
        'model_number',
        'notes',
        'datasheet',
        'installationGuide',
        'drawing',
        'warranty',
        'otherDocument',
    ],
    width: {
        category: 200,
        qty: 200,
        manufacturer: 200,
        model_number: 200,
        notes: 200,
        datasheet: 200,
        installationGuide: 200,
        drawing: 200,
        warranty: 200,
        otherDocument: 200,
    }
};

export const COLUMN_DETAILS_PL_TAB = {
    order: [
        'category',
        'qty',
        'manufacturer',
        'model_number',
        'productFinder',
        'quoteNotes',
        'internalComments',
        'specification',
        'replaceProductId',
        'lotId',
        'history',
        'cost',
        'per',
        'discount',
        'discountedCost',
        'margin',
        'sellPrice',
        'extendedPrice',
        'addDeductAmount',
        'leadTimeValue',
        'leadTimeUnit',
        'quoteNo',
    ],
    width: {
        category: 200,
        qty: 200,
        manufacturer: 200,
        model_number: 200,
        productFinder: 100,
        quoteNotes: 200,
        internalComments: 200,
        specification: 208,
        description: 200,
        replaceProductId: 300,
        lotId: 200,
        history: 200,
        cost: 200,
        per: 145,
        discount: 120,
        discountedCost: 143,
        extendedDiscountCost: 143,
        margin: 100,
        sellPrice: 130,
        extendedPrice: 145,
        addDeductAmount: 200,
        leadTimeValue: 145,
        leadTimeUnit: 145,
        quoteNo: 173,
    }
};

export const COLUMN_DETAILS_BOM_TAB = {
    order: [
        'category',
        'qty',
        'manufacturer',
        'model_number',
        'productFinder',
        'specification',
        'description',
        'product_to_replace',
    ],
    width: {
        category: 200,
        qty: 200,
        manufacturer: 200,
        model_number: 200,
        productFinder: 100,
        specification: 208,
        description: 200,
        product_to_replace: 300,
    }
};

export const PAGE_COLUMN_DETAILS_MAPPING = {
    bom_specifications_tab: COLUMN_DETAILS_BOM_TAB,
    bom_pricing_tab: COLUMN_DETAILS_PL_TAB,
    bom_documents_tab: COLUMN_DETAILS_OM_TAB,
} as const;

export enum SECTION_TYPE_FOR_PAYLOAD {
    MAIN = 'section',
    OPTION = 'substitute_section'
}

export const NO_BOD_MSG = 'Import products or add lines to get started!';

export const ROUNDING_BEHAVIOUR = [
    { id: 'no rounding', name: 'No Rounding' },
    { id: 'sell price', name: 'Sell Price' },
];
export const ROUNDING_PRECISION = [
    { id: '0.05', name: 'Up to nearest $0.05' },
    { id: '0.10', name: 'Up to nearest $0.10' },
    { id: '0.25', name: 'Up to nearest $0.25' },
    { id: '0.50', name: 'Up to nearest $0.50' },
    { id: '1.00', name: 'Up to nearest $1.00' }

];

export const NO_ROUNDING = 'No Rounding';
export const DEFAULT_PRECISION = '0.05';
export const ROUNDING_SELL_PRICE_KEY = 'Sell Price';
export const BODS_COLUMN_MAP: { [index: string]: string } = {
    type: 'category',
    category: 'category',
    qty: 'qty',
    manufacturer: 'manufacturer',
    model_number: 'model_number',
    description: `description`,
    is_hidden: 'is_hidden'
};

export const PRICE_RELATED_COLS = {
    cost: 'cost_cents',
    discountedCost: 'discounted_cost_cents',
    sellPrice: 'sell_price_cents'
};

export const QUOTE_META_DATA_COLS_MAP = {
    ...PRICE_RELATED_COLS,
    margin: 'margin_percentage',
    discount: 'discount_percentage',
    internalComments: 'internal_notes',
    quoteNotes: 'notes',
    sourceQuoteNumber: 'source_quote_number',
    quoteNo: 'source_quote_number',
    leadTimeValue: 'lead_time_value'
};

export const SUBMITTAL_META_DATA_COLS_MAP = {
    notes: 'notes'
};

export const ROW_LEVEL = {
    SECTION: 0,
    PRODUCT: 1,
    COMPONENT: 2
} as const;

export enum SPECIFICATION_VALUES {
    PENDING_ALTERNATE = 'Pending Alternate',
    APPROVED_ALTERNATE = 'Approved Alternate',
    LOST_ALTERNATE = 'Lost Alternate',
    BASIS_OF_DESIGN = 'Basis of Design'
}

export const SPECIFICATION_OPTIONS = [
    { label: 'Basis of Design', value: SPECIFICATION_VALUES.BASIS_OF_DESIGN, type: 'primary' },
    { label: 'Pending Alternate', value: SPECIFICATION_VALUES.PENDING_ALTERNATE, type: 'tertiary' },
    { label: 'Approved Alternate', value: SPECIFICATION_VALUES.APPROVED_ALTERNATE, type: 'success' },
    { label: 'Lost Alternate', value: SPECIFICATION_VALUES.LOST_ALTERNATE, type: 'error' }
] as const;

export type SpecificationValType = (typeof SPECIFICATION_VALUES)[keyof typeof SPECIFICATION_VALUES];
export type SelectedDocType = 'alldocs' | 'datasheet' | 'installGuide' | 'warranty';
export type SelectedSearchType = 'me' | 'company_users' | 'company_group_users';

export const ADD_REUSE_ACCEPTED_FORMATS = ['.pdf']

export enum BOM_TABLES_ENUM {
    ID = 'id',
    TYPE = 'category',
    QTY = 'qty',
    QTY_PER = 'qtyPer',
    MANUFACTURER = 'manufacturer',
    MODEL_NO = 'model_number',
    PRODUCT_FINDER = 'productFinder',
    QUOTE_NOTES = 'quoteNotes',
    SPECIFICATION = 'specification',
    DESCRIPTION = 'description',
    LOT = 'lotId',
    COST = 'cost',
    EXT_COST = 'extendedCost',
    EXT_REVENUE = "extendedRevenue",
    DISCOUNT = 'discount',
    DISCOUNT_COST = 'discountedCost',
    EXT_DISCOUNT_COST = 'extendedDiscountCost',
    MARGIN = 'margin',
    SELL_PRICE = 'sellPrice',
    EXTENDED_PRICE = 'extendedPrice',
    LEAD_TIME_VALUE = 'leadTimeValue',
    LEAD_TIME_MIN = 'leadTimeMin',
    LEAD_TIME_MAX = 'leadTimeMax',
    LEAD_TIME_UNIT = 'leadTimeUnit',
    QUOTE_NO = 'quoteNo',
    LOT_INFO = 'lotInfo',
    IS_HIDDEN = 'is_hidden',
    SECTION = 'section',
    OPTION_SECTION = 'substitute_section',
    PRODUCT_TO_REPLACE = 'replaceProductId',
    PRODUCT_TO_REPLACE_LABEL = 'product_to_replace_label',
    PRODUCT_TO_REPLACE_INFO = 'replaceProductInfo',
    ADD_DEDUCT_AMT = 'addDeductAmount',
    INTERNAL_COMMENTS = 'internalComments',
    HAS_OPTION = 'hasOptions',
    KIT = 'kit',
    IS_KIT = 'is_kit',
    ORDER = 'order',
    HISTORY = 'history',
    IS_HIGHLIGHTED = 'is_highlighted',
    PER = 'per',
    NOTES = 'notes',
    DATASHEET = 'datasheet',
    INSTALLATION_GUIDE = 'installation_guide',
    DRAWING = 'drawing',
    WARRANTY = 'warranty',
    OTHER_DOC = 'other_document',
    PRODUCT_REPLACE_BOM = 'product_to_replace',
    ALL_PAGES_HIDDEN = 'all_pages_hidden',
    SELECTED_DATASHEET_ID = 'selected_datasheet_id'
}

export const ROW_HEIGHT = 36;

export const MESSAGE_CREATE_KIT = 'Line items should be selected from a single section and no kit/component line should be selected.'

export const MESSAGE_CHANGE_SECTION = 'Line items should be selected from a single section and at least one product/kit should be selected.'
export const MESSAGE_HIGHLIGHT_UNHIGHLIGHT = "Atleast one product should be selected."

export const AUTO_SELECT_CLASSESS = '.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth.Mui-focused.MuiInputBase-formControl.MuiInputBase-sizeSmall.MuiInputBase-adornedEnd.MuiAutocomplete-inputRoot';
